import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { formatBytes, type ChatAttachment } from "@xop/dashboard-shared";
import type { JSX } from "react";

/** A file staged on the composer (image thumbnail or file icon) with its name +
 * size and a remove button, or an "Uploading…" spinner. Extracted from
 * `ChatComposer.tsx`. Renders nothing when there is neither. */
export function StagedAttachment({
  staged,
  uploading,
  onClear,
}: {
  staged: { att: ChatAttachment; previewUrl: string | null } | null;
  uploading: boolean;
  onClear: () => void;
}): JSX.Element | null {
  if (!staged && !uploading) return null;
  return (
    <Box
      data-testid="chat-staged-attachment"
      sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, pt: 1.5 }}
    >
      {uploading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
          <CircularProgress size={18} />
          <Typography sx={{ fontSize: 13 }}>Uploading…</Typography>
        </Box>
      ) : staged ? (
        <>
          {staged.previewUrl ? (
            <Box
              component="img"
              src={staged.previewUrl}
              alt={staged.att.filename}
              sx={{ width: 44, height: 44, borderRadius: 1, objectFit: "cover" }}
            />
          ) : (
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 32, color: "chat.accent" }} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", maxWidth: 240 }}>
              {staged.att.filename}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {formatBytes(staged.att.size_bytes)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClear} aria-label="Remove attachment" sx={{ ml: "auto" }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </>
      ) : null}
    </Box>
  );
}
